import { ref, computed } from 'vue';
import type { Ref } from 'vue';
import type { FormElement, RadioGroupProperties, AlternativeItem } from './form.types';

interface UseRadioGroupAlternativesModalOptions {
  selectedElement: Ref<FormElement | null | undefined>;
  updateElement: (updates: Partial<FormElement>) => void;
}

export function useRadioGroupAlternativesModal(options: UseRadioGroupAlternativesModalOptions) {
  const { selectedElement, updateElement } = options;

  const localOptions = ref<AlternativeItem[]>([]);

  const totalCount = computed(() => localOptions.value.length);

  let fallbackIdCounter = 0;

  function createOptionId(): string {
    const cryptoApi = globalThis.crypto;
    if (cryptoApi?.randomUUID) {
      return `radio-${cryptoApi.randomUUID()}`;
    }
    if (cryptoApi?.getRandomValues) {
      const bytes = new Uint8Array(16);
      cryptoApi.getRandomValues(bytes);
      const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
      return `radio-${hex}`;
    }
    fallbackIdCounter += 1;
    return `radio-fallback-${Date.now()}-${fallbackIdCounter}`;
  }

  function initFromElement() {
    const props = selectedElement.value?.properties as RadioGroupProperties | undefined;
    const values = props?.options ?? ['Opção 1', 'Opção 2'];
    localOptions.value = values.map((value) => ({ id: createOptionId(), value }));
  }

  function addOption() {
    localOptions.value = [
      ...localOptions.value,
      { id: createOptionId(), value: `Opção ${localOptions.value.length + 1}` },
    ];
  }

  function removeOption(index: number) {
    if (localOptions.value.length <= 1) return;
    localOptions.value = localOptions.value.filter((_, i) => i !== index);
  }

  function updateOption(index: number, value: string) {
    localOptions.value = localOptions.value.map((opt, i) => (i === index ? { ...opt, value } : opt));
  }

  function save() {
    const currentProps = selectedElement.value?.properties as RadioGroupProperties;
    updateElement({
      properties: {
        ...currentProps,
        options: localOptions.value.map((opt) => opt.value),
      } as RadioGroupProperties,
    });
  }

  return {
    localOptions,
    totalCount,
    initFromElement,
    addOption,
    removeOption,
    updateOption,
    save,
  };
}
